/** @jsxImportSource react */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Badge, Box, Button, Checkbox, Chip, Divider, IconButton, InputAdornment, ListItemIcon, ListSubheader, Menu, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { recordsAPI, viewsAPI } from '../../api/crm'
import { useEntityTypes, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import { selectIsTenantAdmin } from '../../store/slices/tenantSlice'
import { allFields, formatValue } from '../../components/records/fieldValues'
import { useEntityAccess } from '../../hooks/usePermissions'
import {
  DEFAULT_COLUMN_COUNT, describeFilter, emptyListState, filterCount, isGroupable, isSortable, isSummable, sameState, sanitizeConfig, treeFieldOf,
} from '../../components/records/listModel'
import FilterBuilder from '../../components/records/FilterBuilder'
import ColumnChooser from '../../components/records/ColumnChooser'
import SaveViewDialog from '../../components/records/SaveViewDialog'
import RecordBoard from '../../components/records/RecordBoard'
import RecordTree from '../../components/records/RecordTree'
import BulkAssignDialog from '../../components/records/BulkAssignDialog'
import BulkEditDialog from '../../components/records/BulkEditDialog'
import { ME, usePersonLabel } from '../../components/records/PersonInput'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { LookupLink, LookupTitle } from '../../components/records/LookupInput'
import { downloadBlob, entityLabel, entityLabelPlural, formatRelativeTime } from '../../utils/helpers'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import EmptyState from '../../components/common/EmptyState'
import ConfirmDialog from '../../components/common/ConfirmDialog'

const ALL_RECORDS = ''

const RecordListPage = () => {
  const { entityType } = useParams()
  const { forms, isLoading: formsLoading } = useEntityTypes()
  const entityForms = forms.filter((f) => f.entityType === entityType)
  const primaryCode = entityForms[0]?.code
  const { data: form, isLoading: formLoading } = usePublishedForm(primaryCode)

  if (formsLoading || (primaryCode && formLoading)) return <Loading />
  if (!primaryCode) {
    return <EmptyState title={`No published form for ${entityLabelPlural(entityType)}`} description="An admin needs to publish a form for this record type first." />
  }
  if (!form) return <Loading />
  // remount per entity type so paging, filters and the selected view reset
  return <RecordList key={entityType} entityType={entityType} form={form} entityForms={entityForms} />
}

// last selected view per tenant + entity type; a convenience, so storage failures are ignored
const viewStorageKey = (tenantId, entityType) => `crm.view.${tenantId}.${entityType}`
const readStoredView = (key) => {
  try { return localStorage.getItem(key) || ALL_RECORDS } catch { return ALL_RECORDS }
}
const storeView = (key, id) => {
  try { if (id) localStorage.setItem(key, id); else localStorage.removeItem(key) } catch { /* ignore */ }
}

const RecordList = ({ entityType, form, entityForms }) => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const isAdmin = useSelector(selectIsTenantAdmin)
  const queryClient = useQueryClient()
  const notify = useNotify()

  const fields = useMemo(() => allFields(form), [form])
  const access = useEntityAccess(entityType)
  const byKey = useMemo(() => Object.fromEntries(fields.map((f) => [f.fieldKey, f])), [fields])
  const defaultColumns = useMemo(() => fields.filter((f) => !f.hidden).slice(0, DEFAULT_COLUMN_COUNT).map((f) => f.fieldKey), [fields])

  // ?filter.<field>=<value> (e.g. "View all" from a related list) opens "All records" pre-filtered
  const [searchParams] = useSearchParams()
  const urlFilters = useMemo(() => {
    const filters = {}
    searchParams.forEach((value, name) => {
      if (!name.startsWith('filter.')) return
      const spec = name.slice(7)
      const dot = spec.lastIndexOf('.')
      const key = dot > 0 && !byKey[spec] ? spec.slice(0, dot) : spec
      const op = key === spec ? 'eq' : spec.slice(dot + 1)
      if (byKey[key]) filters[key] = { ...(filters[key] || {}), [op]: value }
    })
    return filters
  }, [searchParams, byKey])
  const hasUrlFilters = Object.keys(urlFilters).length > 0

  const storageKey = viewStorageKey(tenantId, entityType)
  const [viewId, setViewId] = useState(() => (hasUrlFilters ? ALL_RECORDS : readStoredView(storageKey)))
  const [state, setState] = useState(emptyListState)
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [anchors, setAnchors] = useState({})
  const [saveDialog, setSaveDialog] = useState(null) // 'new' | 'rename'
  const [deleting, setDeleting] = useState(null)
  const [deletingView, setDeletingView] = useState(false)

  const viewsKey = [tenantId, 'views', entityType]
  const { data: views, isSuccess: viewsLoaded } = useQuery({ queryKey: viewsKey, queryFn: () => viewsAPI.list(entityType) })
  const viewList = views || []
  const selectedView = viewList.find((v) => v.id === viewId)
  // keyed on the saved content, so a background refetch doesn't wipe unsaved changes
  const baseKey = selectedView ? `${selectedView.id}:${JSON.stringify(selectedView.config)}` : ALL_RECORDS
  const baseState = useMemo(() => (selectedView ? sanitizeConfig(selectedView.config, fields) : { ...emptyListState(), filters: urlFilters }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseKey, fields, urlFilters])
  const modified = !sameState(state, baseState)

  // a remembered view that was deleted (or is no longer visible) falls back to all records
  useEffect(() => {
    if (viewId && viewsLoaded && !selectedView) setViewId(ALL_RECORDS)
  }, [viewId, viewsLoaded, selectedView])
  // load a view's configuration when it is selected
  useEffect(() => {
    setState(baseState)
    setSearchInput(baseState.q)
    setPage(0)
  }, [baseState])

  // debounce typing into the search box
  useEffect(() => {
    const handle = setTimeout(() => {
      setState((prev) => (prev.q === searchInput ? prev : { ...prev, q: searchInput }))
      setPage(0)
    }, 300)
    return () => clearTimeout(handle)
  }, [searchInput])

  const columns = (state.columns || defaultColumns).map((k) => byKey[k]).filter(Boolean)
  const groupableFields = fields.filter(isGroupable)
  const summableFields = fields.filter(isSummable)
  const isBoard = state.layout === 'board' && !!state.groupBy
  const treeField = treeFieldOf(fields, entityType)
  // search and filters cut across the tree, so they show as the flat table
  const wantsTree = state.layout === 'tree' && !!treeField
  const switchLayout = (layout) => {
    if (!layout) return
    if (layout === 'board') {
      // sensible defaults: first dropdown to group by, first currency (else number) field to total
      const groupBy = state.groupBy || groupableFields[0]?.fieldKey || null
      const sumField = state.sumField ?? (summableFields.find((f) => f.fieldType === 'CURRENCY') || summableFields[0])?.fieldKey ?? null
      change({ layout, groupBy, sumField })
    } else {
      change({ layout })
    }
  }
  const [sortTarget, sortDir = 'desc'] = state.sort.split(',')

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [tenantId, 'records', entityType, { page, size, ...state }],
    queryFn: () => recordsAPI.list({ entityType, page, size, sort: state.sort, q: state.q, filters: state.filters }),
    placeholderData: keepPreviousData,
    enabled: !isBoard,
  })

  const change = (patch) => {
    setState((prev) => ({ ...prev, ...patch }))
    setPage(0)
  }
  const selectView = (id) => {
    setViewId(id)
    storeView(storageKey, id)
  }
  const toggleSort = (target) => {
    const nextDir = sortTarget === target && sortDir === 'desc' ? 'asc' : 'desc'
    change({ sort: `${target},${nextDir}` })
  }
  const removeFilter = (key, op) => {
    const ops = { ...state.filters[key] }
    delete ops[op]
    const filters = { ...state.filters }
    if (Object.keys(ops).length) filters[key] = ops
    else delete filters[key]
    change({ filters })
  }
  const configOf = (s) => ({ columns: s.columns, filters: s.filters, sort: s.sort, q: s.q.trim(), layout: s.layout, groupBy: s.groupBy, sumField: s.sumField })

  const createView = useMutation({
    mutationFn: ({ name, shared }) => viewsAPI.create({ entityType, name, shared, config: configOf(state) }),
    onSuccess: (view) => {
      queryClient.setQueryData(viewsKey, (prev = []) => [...prev, view])
      selectView(view.id)
      setSaveDialog(null)
      notify(`View “${view.name}” saved`)
    },
  })
  const updateView = useMutation({
    mutationFn: ({ name, shared }) => viewsAPI.update(selectedView.id, {
      name: name ?? selectedView.name, shared: shared ?? selectedView.shared, config: configOf(state),
    }),
    onSuccess: (view) => {
      queryClient.setQueryData(viewsKey, (prev = []) => prev.map((v) => (v.id === view.id ? view : v)))
      setSaveDialog(null)
      notify(`View “${view.name}” updated`)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const deleteView = useMutation({
    mutationFn: () => viewsAPI.remove(selectedView.id),
    onSuccess: () => {
      queryClient.setQueryData(viewsKey, (prev = []) => prev.filter((v) => v.id !== selectedView.id))
      selectView(ALL_RECORDS)
      setDeletingView(false)
      notify('View deleted')
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const deleteRecord = useMutation({
    mutationFn: (record) => recordsAPI.remove(record.id),
    onSuccess: () => {
      setDeleting(null)
      notify(`${entityLabel(entityType)} deleted`)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const exportCsv = useMutation({
    mutationFn: () => recordsAPI.exportCsv({ entityType, sort: state.sort, q: state.q, filters: state.filters, columns: columns.map((f) => f.fieldKey) }),
    onSuccess: ({ blob, filename }) => downloadBlob(blob, filename),
    // errors come back as a Blob (responseType: 'blob'): read the JSON message out of it
    onError: async (e) => {
      let message = getErrorMessage(e)
      try { message = JSON.parse(await e.response.data.text()).detail || message } catch { /* keep generic */ }
      notify(message, 'error')
    },
  })

  const open = (name) => (e) => setAnchors({ [name]: e.currentTarget })
  const close = () => setAnchors({})
  const startNew = (e) => {
    if (entityForms.length > 1) open('new')(e)
    else navigate(`/records/${entityType}/new?form=${encodeURIComponent(entityForms[0].code)}`)
  }

  const mine = viewList.filter((v) => !v.shared)
  const shared = viewList.filter((v) => v.shared)
  const activeFilters = filterCount(state.filters)
  const narrowed = activeFilters > 0 || state.q.trim() !== ''

  // "Mine" = the first person field (usually Owner) is the signed-in user; "$me" keeps saved views per-viewer
  const personFields = fields.filter((f) => f.fieldType === 'USER')
  const mineField = personFields[0]
  const isMine = !!mineField && state.filters[mineField.fieldKey]?.eq === ME
  const toggleMine = () => {
    const filters = { ...state.filters }
    if (isMine) delete filters[mineField.fieldKey]
    else filters[mineField.fieldKey] = { eq: ME }
    change({ filters })
  }
  const personLabel = usePersonLabel()

  // row selection for bulk actions; cleared whenever the visible rows change
  const [selected, setSelected] = useState(() => new Set())
  const [assignOpen, setAssignOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const pageIds = data?.items.map((r) => r.id) || []
  const pageKey = pageIds.join(',')
  useEffect(() => { setSelected(new Set()) }, [pageKey])
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const reportBulk = (verb, result) => {
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
    setSelected(new Set())
    if (result.failed.length === 0) notify(`${result.succeeded} ${result.succeeded === 1 ? 'record' : 'records'} ${verb}`)
    else notify(`${result.succeeded} ${verb}, ${result.failed.length} failed: ${result.failed[0].error}`, 'warning')
  }
  const bulkAssign = useMutation({
    mutationFn: ({ fieldKey, person }) => recordsAPI.bulkUpdate([...selected], { [fieldKey]: person }),
    onSuccess: (result) => { setAssignOpen(false); reportBulk('updated', result) },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const bulkEdit = useMutation({
    mutationFn: ({ fieldKey, value }) => recordsAPI.bulkUpdate([...selected], { [fieldKey]: value }),
    onSuccess: (result) => { setEditOpen(false); reportBulk('updated', result) },
  })
  // fields a bulk edit may set: editable for the member's role, not computed
  const editableFields = fields.filter((f) => !f.readOnly && f.fieldType !== 'ROLLUP' && access.field(f.fieldKey) === 'EDIT')
  const bulkDelete = useMutation({
    mutationFn: () => recordsAPI.bulkDelete([...selected]),
    onSuccess: (result) => { setBulkDeleting(false); reportBulk('deleted', result) },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  return (
    <>
      <PageHeader
        title={entityLabelPlural(entityType)}
        subtitle={isBoard ? (selectedView ? selectedView.name : 'Board') : data ? `${data.totalItems} ${data.totalItems === 1 ? 'record' : 'records'}${selectedView ? ` · ${selectedView.name}` : ''}` : ' '}
        actions={(
          <>
            <Tooltip title="Download the current list (search, filters, columns) as CSV">
              <span>
                <Button startIcon={<FileDownloadOutlinedIcon />} onClick={() => exportCsv.mutate()} disabled={exportCsv.isPending || (!isBoard && !data?.totalItems)}>
                  {exportCsv.isPending ? 'Exporting…' : 'Export'}
                </Button>
              </span>
            </Tooltip>
            {isAdmin && <Button startIcon={<FileUploadOutlinedIcon />} onClick={() => navigate(`/records/${entityType}/import`)}>Import</Button>}
            {access.canCreate && <Button variant="contained" startIcon={<AddIcon />} onClick={startNew}>New {entityLabel(entityType).toLowerCase()}</Button>}
          </>
        )}
      />
      <Menu anchorEl={anchors.new} open={!!anchors.new} onClose={close}>
        {entityForms.map((f) => (
          <MenuItem key={f.code} onClick={() => navigate(`/records/${entityType}/new?form=${encodeURIComponent(f.code)}`)}>{f.name}</MenuItem>
        ))}
      </Menu>

      {/* toolbar: view · search · filters · columns · save */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="outlined" color="inherit" endIcon={<ExpandMoreIcon />} onClick={open('views')}
          startIcon={selectedView?.shared ? <PeopleOutlineIcon /> : <BookmarkBorderIcon />} sx={{ borderColor: 'divider', maxWidth: 240 }}>
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {selectedView ? selectedView.name : 'All records'}
          </Box>
          {modified && <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>*</Box>}
        </Button>
        <Menu anchorEl={anchors.views} open={!!anchors.views} onClose={close}>
          <MenuItem selected={!selectedView} onClick={() => { selectView(ALL_RECORDS); close() }}>All records</MenuItem>
          {mine.length > 0 && <ListSubheader>My views</ListSubheader>}
          {mine.map((v) => (
            <MenuItem key={v.id} selected={v.id === viewId} onClick={() => { selectView(v.id); close() }}>
              <ListItemIcon><BookmarkBorderIcon fontSize="small" /></ListItemIcon>{v.name}
            </MenuItem>
          ))}
          {shared.length > 0 && <ListSubheader>Shared</ListSubheader>}
          {shared.map((v) => (
            <MenuItem key={v.id} selected={v.id === viewId} onClick={() => { selectView(v.id); close() }}>
              <ListItemIcon><PeopleOutlineIcon fontSize="small" /></ListItemIcon>{v.name}
            </MenuItem>
          ))}
        </Menu>

        <TextField
          placeholder={`Search ${entityLabelPlural(entityType).toLowerCase()}`}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          fullWidth={false}
          sx={{ flex: '1 1 220px', maxWidth: 360 }}
          inputProps={{ 'aria-label': 'Search records', maxLength: 200 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />

        <Badge badgeContent={activeFilters} color="primary">
          <Button variant="outlined" color="inherit" startIcon={<FilterListIcon />} onClick={open('filters')} sx={{ borderColor: 'divider' }}>Filters</Button>
        </Badge>
        <FilterBuilder anchorEl={anchors.filters} open={!!anchors.filters} onClose={close} fields={fields}
          filters={state.filters} onApply={(filters) => change({ filters })} />

        <Button variant="outlined" color="inherit" startIcon={<ViewColumnOutlinedIcon />} onClick={open('columns')} sx={{ borderColor: 'divider' }}>Columns</Button>
        <ColumnChooser anchorEl={anchors.columns} open={!!anchors.columns} onClose={close} fields={fields}
          columns={state.columns || defaultColumns} onChange={(cols) => change({ columns: cols })} onReset={() => change({ columns: null })} />

        {mineField && (
          <Chip icon={<PersonOutlineIcon />} label="Mine" clickable color={isMine ? 'primary' : 'default'} variant={isMine ? 'filled' : 'outlined'}
            onClick={toggleMine} aria-pressed={isMine} title={`Only records where ${mineField.label} is you`} />
        )}
        {(groupableFields.length > 0 || treeField) && (
          <ToggleButtonGroup size="small" exclusive value={isBoard ? 'board' : wantsTree ? 'tree' : 'table'} onChange={(_, v) => switchLayout(v)} aria-label="Layout">
            <ToggleButton value="table" aria-label="Table"><Tooltip title="Table"><TableRowsOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>
            {groupableFields.length > 0 && <ToggleButton value="board" aria-label="Board"><Tooltip title="Board"><ViewKanbanOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>}
            {treeField && <ToggleButton value="tree" aria-label="Tree"><Tooltip title="Tree"><AccountTreeOutlinedIcon fontSize="small" /></Tooltip></ToggleButton>}
          </ToggleButtonGroup>
        )}
        {isBoard && (
          <>
            <TextField select label="Group by" value={state.groupBy} onChange={(e) => change({ groupBy: e.target.value })} fullWidth={false} sx={{ minWidth: 140 }}>
              {groupableFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
            </TextField>
            <TextField select label="Total" value={state.sumField || ''} onChange={(e) => change({ sumField: e.target.value || null })} fullWidth={false}
              sx={{ minWidth: 130 }} SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}>
              <MenuItem value=""><em>Count only</em></MenuItem>
              {summableFields.map((f) => <MenuItem key={f.fieldKey} value={f.fieldKey}>{f.label}</MenuItem>)}
            </TextField>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        {modified && <Button onClick={() => { setState(baseState); setSearchInput(baseState.q) }}>Reset</Button>}
        {selectedView?.editable && modified && (
          <Button variant="outlined" onClick={() => updateView.mutate({})} disabled={updateView.isPending}>Save view</Button>
        )}
        <Button onClick={open('saveMenu')} endIcon={<ExpandMoreIcon />}>{selectedView ? 'View' : 'Save view'}</Button>
        <Menu anchorEl={anchors.saveMenu} open={!!anchors.saveMenu} onClose={close} disableRestoreFocus>
          <MenuItem onClick={() => { close(); setSaveDialog('new') }}>Save as new view…</MenuItem>
          {selectedView?.editable && <MenuItem onClick={() => { close(); setSaveDialog('rename') }}>Rename / sharing…</MenuItem>}
          {selectedView?.editable && <Divider />}
          {selectedView?.editable && <MenuItem onClick={() => { close(); setDeletingView(true) }} sx={{ color: 'error.main' }}>Delete view</MenuItem>}
        </Menu>
      </Box>

      {activeFilters > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
          {Object.entries(state.filters).flatMap(([key, ops]) => Object.entries(ops).map(([op, value]) => (
            byKey[key] && (
              <Chip key={`${key}.${op}`} onDelete={() => removeFilter(key, op)} onClick={open('filters')}
                label={byKey[key].fieldType === 'LOOKUP'
                  ? <>{byKey[key].label} is <LookupTitle id={value} /></>
                  : byKey[key].fieldType === 'USER'
                    ? `${byKey[key].label} is ${personLabel(value)}`
                    : describeFilter(byKey[key], op, value)} />
            )
          )))}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorMessage(error)} {getErrorList(error).join('; ')}
        </Alert>
      )}

      {wantsTree && narrowed && (
        <Alert severity="info" sx={{ mb: 2 }}>Search and filters show matching records as a table; clear them to see the tree.</Alert>
      )}
      {wantsTree && !narrowed ? (
        <RecordTree entityType={entityType} treeField={treeField} sort={state.sort} formCode={form.code}
          columns={columns.filter((f) => f.fieldKey !== treeField.fieldKey && f.fieldKey !== fields.find((x) => x.fieldType === 'TEXT')?.fieldKey).slice(0, 3)} />
      ) : isBoard ? (
        <RecordBoard
          entityType={entityType}
          state={state}
          byKey={byKey}
          // card shows the chosen columns minus the grouping/total fields (shown elsewhere on the card)
          cardFields={columns.filter((f) => f.fieldKey !== state.groupBy && f.fieldKey !== state.sumField && f.fieldKey !== fields.find((x) => x.fieldType === 'TEXT')?.fieldKey).slice(0, 3)}
          onOpen={(record) => navigate(`/records/${entityType}/${record.id}`)}
          canMove={(record) => access.canEdit(record, fields)}
        />
      ) : (
      <TableContainer component={Paper} variant="outlined" sx={{ opacity: isFetching && !isLoading ? 0.7 : 1, transition: 'opacity .2s' }}>
        {isLoading ? <Loading /> : data && data.items.length === 0 ? (
          <EmptyState
            icon={<InboxOutlinedIcon />}
            title={narrowed ? 'No matches' : `No ${entityLabelPlural(entityType).toLowerCase()} yet`}
            description={narrowed ? 'Try another search or fewer filters.' : `Create the first ${entityLabel(entityType).toLowerCase()} to get started.`}
            action={narrowed && <Button onClick={() => { change({ filters: {}, q: '' }); setSearchInput('') }}>Clear search and filters</Button>}
          />
        ) : data && (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected} indeterminate={selected.size > 0 && !allSelected}
                        onChange={() => setSelected(allSelected ? new Set() : new Set(pageIds))} inputProps={{ 'aria-label': 'Select all on this page' }} />
                    </TableCell>
                    {selected.size > 0 ? (
                      // bulk actions take the header row's place, so the rows below don't move
                      <TableCell colSpan={columns.length + 3} sx={{ py: 0.25 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>{selected.size} selected</Typography>
                          {personFields.length > 0 && access.edit !== 'NONE' && <Button startIcon={<PersonOutlineIcon />} onClick={() => setAssignOpen(true)}>Assign to…</Button>}
                          {access.edit !== 'NONE' && editableFields.length > 0 && <Button startIcon={<EditOutlinedIcon />} onClick={() => setEditOpen(true)}>Edit field…</Button>}
                          {access.delete !== 'NONE' && <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setBulkDeleting(true)}>Delete</Button>}
                          <Button onClick={() => setSelected(new Set())}>Clear</Button>
                        </Box>
                      </TableCell>
                    ) : (
                      <>
                    {columns.map((f) => (
                      <TableCell key={f.fieldKey} sx={{ whiteSpace: 'nowrap' }}>
                        {isSortable(f) ? (
                          <TableSortLabel active={sortTarget === `data.${f.fieldKey}`} direction={sortTarget === `data.${f.fieldKey}` ? sortDir : 'asc'}
                            onClick={() => toggleSort(`data.${f.fieldKey}`)}>
                            {f.label}
                          </TableSortLabel>
                        ) : f.label}
                      </TableCell>
                    ))}
                    {['updatedOn', 'createdOn'].map((col) => (
                      <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                        <TableSortLabel active={sortTarget === col} direction={sortTarget === col ? sortDir : 'desc'} onClick={() => toggleSort(col)}>
                          {col === 'updatedOn' ? 'Updated' : 'Created'}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="right" />
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.items.map((record) => (
                    <TableRow key={record.id} hover selected={selected.has(record.id)} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/records/${entityType}/${record.id}`)}>
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox size="small" checked={selected.has(record.id)} onChange={() => toggleRow(record.id)} inputProps={{ 'aria-label': `Select ${record.title}` }} />
                      </TableCell>
                      {columns.map((f, i) => (
                        <TableCell key={f.fieldKey} sx={{ maxWidth: 260, fontWeight: i === 0 ? 500 : undefined }}>
                          <Typography variant="body2" noWrap fontWeight="inherit">
                            {f.fieldType === 'LOOKUP' || f.fieldType === 'USER' ? <LookupLink refValue={record.lookups?.[f.fieldKey]} /> : formatValue(f, record.data[f.fieldKey])}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatRelativeTime(record.updatedOn)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>{formatRelativeTime(record.createdOn)}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {access.canDelete(record, fields) && (
                          <Tooltip title="Delete">
                            <IconButton onClick={() => setDeleting(record)} aria-label="Delete record"><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={data.totalItems}
              page={page}
              rowsPerPage={size}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0) }}
            />
          </>
        )}
      </TableContainer>
      )}

      <BulkAssignDialog open={assignOpen} count={selected.size} personFields={personFields} busy={bulkAssign.isPending}
        onAssign={(fieldKey, person) => bulkAssign.mutate({ fieldKey, person })} onClose={() => setAssignOpen(false)} />
      <BulkEditDialog open={editOpen} count={selected.size} fields={editableFields} busy={bulkEdit.isPending}
        onApply={(fieldKey, value) => bulkEdit.mutate({ fieldKey, value })} onClose={() => setEditOpen(false)} />
      <ConfirmDialog open={bulkDeleting} title={`Delete ${selected.size} ${selected.size === 1 ? 'record' : 'records'}?`}
        message="They will be removed from all lists." confirmLabel="Delete" destructive busy={bulkDelete.isPending}
        onConfirm={() => bulkDelete.mutate()} onClose={() => setBulkDeleting(false)} />
      <SaveViewDialog
        open={!!saveDialog}
        title={saveDialog === 'rename' ? 'Rename view' : 'Save view'}
        initialName={saveDialog === 'rename' ? selectedView?.name : ''}
        initialShared={saveDialog === 'rename' ? selectedView?.shared : false}
        canShare={isAdmin}
        busy={createView.isPending || updateView.isPending}
        error={saveDialog === 'rename' ? updateView.error : createView.error}
        onSave={(values) => (saveDialog === 'rename' ? updateView.mutate(values) : createView.mutate(values))}
        onClose={() => setSaveDialog(null)}
      />
      <ConfirmDialog
        open={deletingView}
        title="Delete view?"
        message={`“${selectedView?.name}” will be removed${selectedView?.shared ? ' for everyone' : ''}. Records are not affected.`}
        confirmLabel="Delete"
        destructive
        busy={deleteView.isPending}
        onConfirm={() => deleteView.mutate()}
        onClose={() => setDeletingView(false)}
      />
      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${entityLabel(entityType).toLowerCase()}?`}
        message="This record will be removed from all lists."
        confirmLabel="Delete"
        destructive
        busy={deleteRecord.isPending}
        onConfirm={() => deleteRecord.mutate(deleting)}
        onClose={() => setDeleting(null)}
      />
    </>
  )
}

export default RecordListPage
